import React, { useState, useContext } from 'react';
import { Constants } from '../Common/Constants';
import { calculateTax } from '../Common/Utilities';
import { STATUS_ATTRIBUTE, VIEW_ATTRIBUTE } from './InvoiceDocumentEditorFunctions';


const InvoiceContext = React.createContext();

export const InvoiceDocumentEditorContext = ({ children, ...props }) => {

	const [state, setState] = useState({
		isDirty: false,
		lineDirty: false,
		readOnly: true,
		source: props.info.source || props.info.rowData?.invoiceSource || '',
		mode: props.info.mode || "View",
		title: '',
		openVendorDialog: false,
		invoiceDocument:null,
		openClientDialog: false,
		openPOHeaderDialog: false,
		openLevel2Dialog: false,
		openLevel3Dialog: false,
		openCostCodesDialog: false,
		openPOHeaderResourceDialog: false,
		openLevel2ResourceDialog: false,
		openChangeVendorConfirmation: false,
		openReplaceInvoiceDocumentEditorDialog: false,
		openInvoiceDocumentRoutingHistoryDialog: false,
		openReplaceFileScanInProgress: (props.info.mode === "Replace" && true) || false,
		vendor: null,
		inProgress: false,
		replacedInvoiceDocument: null,
		serviceTermStartDate: null,
		serviceTermEndDate: null,
		isInvoiceNumberDuplicate: false,
		uncollapsed: true,
		displayInvoiceDocumentViewer: "",
		openDeleteConfirmation: false,
		deleteRowData: null,
		isGridDataValid: false,
		taxes: [],
		originalInvoiceDocumentStatus: 0,
		originalInvoiceStatusIdForGadgetLoading: 0,
		routingHistoryData: [],
		userRoleName: '',
		source: '',
		invoiceDocumentImage: null,
		uiViewAttribute: VIEW_ATTRIBUTE.READ_ONLY,
		uiStatusAttribute: STATUS_ATTRIBUTE.NORMAL,
		headerTaxCode: '',
		capabilities: {
			canPostToERP: false,
			canRepostToERP: false,
			canEditInvoice: false,
			canOverridInvoice: false,
			canRepost: false,
			hasDifferentCompany: false,
			canDelete: false,
			canPostCreditMemo:false
		},
		partialInvoiceStatus: props?.info?.repostInfo,
		updatedRowFields:null
	});
	const [partialPostSeqIdsMap, setPartialPostSeqIdsMap] = useState(null);
	const [title, setTitle] = useState(null);

	const [dialog, setDialog] = React.useState({
		showPartialPostDialog:false
	})

	const [successfullSave, setSuccessfullSave] = React.useState(false);

	// #region methods
	const setDetailsOnVendorChange = React.useCallback(() => {
		const details = state.invoiceDocument?.invoiceDocumentEditorDetails?.map(function (detail, index) {
			detail.ioNumber = "";
			detail.poAmount = 0.00;
			detail.consumedAmount = 0.00;
			detail.deliveryAmount = 0.00;
			detail.clientCode = "";
			detail.taxCode = "";
			detail.taxAmount = 0.00;
			detail.ioDescription = "";
			detail.mediaType = "";
			detail.siteName = "";
			return detail;
		});
		setState({ ...state, invoiceDocument: { ...state.invoiceDocument, invoiceDocumentEditorDetails: details } })
	})

	const getCalculatedTax = (taxeList,taxCode,row) => {
		var taxes = taxeList.filter(t => t.taxCode === taxCode);
		let totalTax = 0.00;
		taxes.forEach(tax => {
			let calculatedTaxAmount =  calculateTax(tax, row.ioInvoiceAmount);

			calculatedTaxAmount = parseFloat(calculatedTaxAmount.toFixed(2));
			totalTax += calculatedTaxAmount
		});
		return totalTax;
	}

	const calculateTaxAmountForRow = (taxCode, row, taxesList) => {
		let originalAmount = 0;

		try {
			if (row.taxAmount && !isNaN(row.taxAmount))
				originalAmount = parseFloat(row.taxAmount);
		} catch (ex) { }

		row.taxAmount = 0.00;
		row.invoiceDocumentDetailTaxes = [];

		if (!taxCode) return row;

		const taxes = taxesList.filter(t => t.taxCode === taxCode);

		let totalTaxBaseOnIoAmount = taxes
			.map((e) => calculateTax(e, row.ioInvoiceAmount))
			.reduce((prev, next) => prev + next, 0);

		taxes.forEach(tax => {
			let calculatedTaxAmount =
				row.isTaxManuallyChanged
					? (row.ioInvoiceAmount === 0
						? 0
						: (originalAmount * calculateTax(tax, row.ioInvoiceAmount)) / totalTaxBaseOnIoAmount)
					: isNaN(row.ioInvoiceAmount)
						? 0.00
						: calculateTax(tax, row.ioInvoiceAmount);

			calculatedTaxAmount = parseFloat(calculatedTaxAmount.toFixed(2));

			row.invoiceDocumentDetailTaxes.push({
				companyCode: row.companyCode,
				invoiceDocumentId: row.invoiceDocumentId,
				seqId: row.seqId,
				taxCode: tax.taxCode,
				taxTypeCode: tax.taxTypeCode,
				taxPrc: tax.otherTaxAmt,
				taxAmount: calculatedTaxAmount
			});
		});

		if (row.isTaxManuallyChanged) {
			row.taxAmount = originalAmount;
			if (row.taxAmount === 0) {
				row.invoiceDocumentDetailTaxes = [];
				row.taxCode = '';
			}
		} else {
			row.taxAmount = row.invoiceDocumentDetailTaxes
				.map(item => item.taxAmount)
				.reduce((prev, next) => prev + next, 0);

			row.taxAmount = parseFloat(row.taxAmount.toFixed(2));
		}

		return row;
	};

	const applyTaxCodeToAllLines = (newTaxCode) => {

		setState(prevState => {

			const taxesList = prevState.invoiceDocument?.taxes || [];
			const readOnlyRow = prevState.readOnly;
			let maxApprovalLevelBase = prevState.invoiceDocument?.companyApprovalConfiguration?.routingNumberOfApprovalLevels || 0;
			const maxApprovalStatus = (maxApprovalLevelBase * 20) + 50;

			const updatedDetails =
				prevState.invoiceDocument.invoiceDocumentEditorDetails.map(detail => {

					const lineStatus = Number(detail.lineStatus);

					const baseEditable =
						(detail.hasAuthority === 'Y' && detail.allowInvoiceCorrection === 'Y') ||
						lineStatus <= 50 ||
						lineStatus === 180 ||
						lineStatus == maxApprovalStatus || lineStatus == 170;

					const rowEnabled = !readOnlyRow && baseEditable;

					if (!rowEnabled) return detail;

					const updatedRow = {
						...detail,
						taxCode: newTaxCode,
						isTaxManuallyChanged: false
					};

					return calculateTaxAmountForRow(
						newTaxCode,
						updatedRow,
						taxesList
					);
				});

			return {
				...prevState,
				isDirty: true,
				invoiceDocument: {
					...prevState.invoiceDocument,
					invoiceDocumentEditorDetails: updatedDetails
				}
			};
		});
	};

	
	// #endregion methods

	return (<InvoiceContext.Provider value={{
		state: state, setState: setState,
		setDetailsOnVendorChange, getTitle, getCalculatedTax,
		successfullSave, setSuccessfullSave,
		dialog, setDialog, partialPostSeqIdsMap, setPartialPostSeqIdsMap,
		setTitle, title, applyTaxCodeToAllLines, calculateTaxAmountForRow
	}}>
		{children}
	</InvoiceContext.Provider>);
}

export const useInvoice = () => useContext(InvoiceContext);





const getTitle = (mode) => {
	var title;
	switch (mode) {
		case "View":
		case "EditDates":
			title = "View Invoice";
			break;
		case "PartialPost":
			title = "Invoice – Partial Posting";
			break;
		case "CreditMemo":
			title = "Credit Memo";
			break;
		case "Test":
			title = "View Invoice (Test)";
			break;
		case "UserEntry":
			title = "User Entry";
			break;
		case "ManualEntry":
			title = "Manual Entry";
			break;
		case "MakeCorrections":
			title = "Make Corrections";
			break;
		case "Replace":
			title = "Replace Invoice";
			break;
		default:
			title = "";
	}
	return title;
}